import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [allTags, setAllTags] = useState([]);
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const { lang = 'en' } = useParams();
  const { t } = useTranslation('blog');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      if (error) throw error;

      setBlogs(data || []);

      // Extract unique tags
      const tags = new Set();
      (data || []).forEach(blog => {
        (blog.tags || []).forEach(tag => tags.add(tag));
      });
      setAllTags(Array.from(tags).sort());
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching blogs:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort blogs
  const filteredBlogs = useMemo(() => {
    let result = [...blogs];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(blog =>
        blog.title.toLowerCase().includes(term) ||
        blog.excerpt?.toLowerCase().includes(term) ||
        blog.content?.toLowerCase().includes(term) ||
        (blog.tags || []).some(tag => tag.toLowerCase().includes(term))
      );
    }

    // Tag filter
    if (selectedTag !== 'all') {
      result = result.filter(blog => (blog.tags || []).includes(selectedTag));
    }

    // Sort
    switch (sortBy) {
      case 'oldest':
        result.sort((a, b) => new Date(a.published_at) - new Date(b.published_at));
        break;
      case 'popular':
        result.sort((a, b) => (b.views || 0) - (a.views || 0));
        break;
      case 'reading_time':
        result.sort((a, b) => (a.reading_time || 5) - (b.reading_time || 5));
        break;
      case 'newest':
      default:
        result.sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
        break;
    }

    return result;
  }, [blogs, searchTerm, selectedTag, sortBy]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(lang === 'da' ? 'da-DK' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTag('all');
    setSortBy('newest');
  };

  const hasActiveFilters = searchTerm || selectedTag !== 'all' || sortBy !== 'newest';

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section - White */}
      <section className="bg-white pt-32 pb-16" data-section="blog-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="text-secondary-accent text-sm font-semibold mb-4 uppercase tracking-wider flex items-center justify-center gap-2">
              <span className="w-2 h-2 bg-secondary-accent rounded-full"></span>
              {t('hero.badge')}
            </p>
            <h1 className="text-5xl lg:text-6xl font-bold text-text-primary mb-6">
              {t('hero.title')}
            </h1>
            <p className="text-xl text-grey-accent max-w-2xl mx-auto">
              {t('hero.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Filter Section */}
      <section className="bg-[#F9F9F9] py-6 sticky top-20 z-40 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Search Input */}
            <div className="relative flex-1 w-full lg:max-w-md">
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-grey-accent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={t('search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary-accent/20 focus:border-secondary-accent transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-grey-accent hover:text-text-primary"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter Controls */}
            <div className="flex flex-wrap gap-3 items-center justify-center lg:justify-end">
              {/* Tag Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsTagDropdownOpen(!isTagDropdownOpen)}
                  className={`flex items-center gap-2 px-4 py-3 bg-white border rounded-xl transition-all ${selectedTag !== 'all'
                    ? 'border-secondary-accent text-secondary-accent'
                    : 'border-gray-200 text-grey-accent hover:border-gray-300'
                    }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span className="text-sm font-medium">
                    {selectedTag === 'all' ? t('filters.allTags') : selectedTag}
                  </span>
                  <svg className={`w-4 h-4 transition-transform ${isTagDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isTagDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsTagDropdownOpen(false)}
                    />
                    <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-2 max-h-64 overflow-y-auto">
                      <button
                        onClick={() => {
                          setSelectedTag('all');
                          setIsTagDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${selectedTag === 'all' ? 'text-secondary-accent font-medium' : 'text-grey-accent'
                          }`}
                      >
                        {t('filters.allTags')}
                      </button>
                      {allTags.map((tag) => (
                        <button
                          key={tag}
                          onClick={() => {
                            setSelectedTag(tag);
                            setIsTagDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${selectedTag === tag ? 'text-secondary-accent font-medium' : 'text-grey-accent'
                            }`}
                        >
                          {tag}
                        </button>
                      ))}
                      {allTags.length === 0 && (
                        <p className="px-4 py-2 text-sm text-grey-accent/50">{t('filters.noTagsAvailable')}</p>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Sort Dropdown */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none px-4 py-3 pr-10 bg-white border border-gray-200 rounded-xl text-sm text-grey-accent focus:outline-none focus:ring-2 focus:ring-secondary-accent/20 focus:border-secondary-accent cursor-pointer"
                >
                  <option value="newest">{t('sort.newest')}</option>
                  <option value="oldest">{t('sort.oldest')}</option>
                  <option value="popular">{t('sort.popular')}</option>
                  <option value="reading_time">{t('sort.readingTime')}</option>
                </select>
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-accent pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {t('filters.clear')}
                </button>
              )}
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="text-sm text-grey-accent">{t('search.showing')}</span>
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-secondary-accent/10 text-secondary-accent rounded-full text-sm">
                  {t('search.searchLabel')} "{searchTerm}"
                  <button onClick={() => setSearchTerm('')} className="hover:text-secondary-accent/70">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              )}
              {selectedTag !== 'all' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-secondary-accent/10 text-secondary-accent rounded-full text-sm">
                  {t('search.tagLabel')} {selectedTag}
                  <button onClick={() => setSelectedTag('all')} className="hover:text-secondary-accent/70">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              )}
              <span className="text-sm text-grey-accent ml-2">
                {filteredBlogs.length} {filteredBlogs.length === 1 ? t('search.articleFound') : t('search.articlesFound')}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary-accent"></div>
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div className="text-center py-20">
              <svg className="w-16 h-16 mx-auto text-grey-accent/30 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2" />
              </svg>
              <h3 className="text-xl font-semibold text-text-primary mb-2">
                {blogs.length === 0 ? t('empty.noPostsTitle') : t('empty.noMatchTitle')}
              </h3>
              <p className="text-grey-accent mb-4">
                {blogs.length === 0
                  ? t('empty.noPostsDescription')
                  : t('empty.noMatchDescription')}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-accent text-white rounded-xl hover:bg-opacity-90 transition-all"
                >
                  {t('filters.clearAllFilters')}
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog, index) => (
                <motion.div
                  key={blog.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link to={`/${lang}/blogs/${blog.slug}`} className="group block h-full">
                    <article className="bg-[#F9F9F9] rounded-2xl overflow-hidden h-full flex flex-col hover:shadow-xl transition-all duration-300 group-hover:scale-[1.02]">
                      {/* Cover Image */}
                      <div className="aspect-video bg-[#EDEDED] relative overflow-hidden">
                        {blog.cover_image ? (
                          <img
                            src={blog.cover_image}
                            alt={blog.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-16 h-16 text-grey-accent/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2" />
                            </svg>
                          </div>
                        )}
                        {/* Reading time badge */}
                        <div className="absolute top-4 right-4 bg-black/70 text-white text-xs px-3 py-1 rounded-full">
                          {blog.reading_time || 5} {t('cards.minRead')}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6 flex flex-col flex-grow">
                        {/* Tags */}
                        {blog.tags && blog.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {blog.tags.slice(0, 2).map((tag) => (
                              <span
                                key={tag}
                                className="text-xs font-medium text-secondary-accent bg-secondary-accent/10 px-2 py-1 rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        <h2 className="text-xl font-bold text-text-primary mb-3 group-hover:text-secondary-accent transition-colors line-clamp-2">
                          {blog.title}
                        </h2>

                        <p className="text-grey-accent text-sm mb-4 flex-grow line-clamp-3">
                          {blog.excerpt || blog.content?.substring(0, 150) + '...'}
                        </p>

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-secondary-accent rounded-full flex items-center justify-center text-white text-sm font-bold">
                              {blog.author_name?.charAt(0) || 'A'}
                            </div>
                            <span className="text-sm text-grey-accent">{blog.author_name || 'Admin'}</span>
                          </div>
                          <span className="text-sm text-grey-accent">
                            {formatDate(blog.published_at)}
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blogs;
